import React, { useRef, useState, useCallback, useEffect } from "react";
import { Box, Alert } from "@mui/material";

import { signUpload, verifyUpload, listMedia } from "../../api";
import { toImgSrcKey } from "../../utils/cdn";

import PickerToolbar from "./PickerToolbar";
import DropZone from "./DropZone";
import PreviewGrid from "./PreviewGrid";
import MediaLibraryDialog from "./MediaLibraryDialog";

import {
    MAX_BYTES,
    ALLOW,
    normalizeKey,
    uniqPush,
    moveToFront,
    isVerifyOk,
    dedupeByKey,
} from "./hooks/helpers";

import styles from "./styles/styles.module.scss";

function toDisplaySrc(img) {
    const raw = typeof img === "string" ? img : img?.url || img?.key || "";
    if (!raw) return "";
    return toImgSrcKey(raw);
}

export default function ImagePicker({
                                        value = [],
                                        onChange,
                                        single = false,
                                        accept = "image/*",
                                        thumbHeight = 260,

                                        // Variant A: S3_PREFIX=bsp => folder/prefix БЕЗ bsp/
                                        folder = "misc",
                                        libraryPrefix = "",

                                        title = "Зображення",
                                    }) {
    const inputRef = useRef(null);

    // важливо: тримати актуальне value, щоб не “зникало” під час async upload
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = Array.isArray(value) ? value : [];
    }, [value]);

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const [uploadingKeys, setUploadingKeys] = useState(() => new Set());

    const [libraryOpen, setLibraryOpen] = useState(false);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const [libraryItems, setLibraryItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(undefined);
    const [search, setSearch] = useState("");

    const resetInput = () => {
        if (inputRef.current) inputRef.current.value = "";
    };

    // safe updater: підтримує onChange як setState
    const applyChange = useCallback(
        (updater) => {
            if (typeof onChange !== "function") return;

            const base = Array.isArray(valueRef.current) ? valueRef.current : [];
            const next = typeof updater === "function" ? updater(base) : updater;
            if (next == null) return;
            if (!Array.isArray(next)) return;
            onChange(next.map(normalizeKey).filter(Boolean));
        },
        [onChange]
    );

    const addUploading = (key) => {
        const k = String(key || "");
        if (!k) return;
        setUploadingKeys((prev) => {
            const n = new Set(prev);
            n.add(k);
            return n;
        });
    };

    const removeUploading = (key) => {
        const k = String(key || "");
        if (!k) return;
        setUploadingKeys((prev) => {
            const n = new Set(prev);
            n.delete(k);
            return n;
        });
    };

    /* ===================== UPLOAD ===================== */
    const handleFiles = useCallback(
        async (files) => {
            const list = Array.from(files || []);
            if (!list.length) return;

            setErr("");
            setBusy(true);

            try {
                // базове значення з ref, щоб не було stale state
                let local = single ? [] : [...(valueRef.current || [])];

                for (const file of list) {
                    const inferredType =
                        file.type ||
                        (/\.(png)$/i.test(file.name) ? "image/png" :
                            /\.(jpe?g)$/i.test(file.name) ? "image/jpeg" :
                                /\.(webp)$/i.test(file.name) ? "image/webp" :
                                    "application/octet-stream");
                    const type = inferredType;

                    if (!ALLOW.includes(type)) {
                        throw new Error("Непідтримуваний тип (PNG/JPG/WEBP)");
                    }
                    if (file.size > MAX_BYTES) {
                        throw new Error("Файл завеликий (>5MB)");
                    }

                    const presign = await signUpload({
                        filename: file.name,
                        contentType: type,
                        folder, // <— ВАЖЛИВО: БЕЗ bsp/
                    });

                    if (!presign?.putUrl || !presign?.key) {
                        throw new Error("Presign error");
                    }

                    const key = normalizeKey(presign.key);
                    if (!key) throw new Error("Invalid key from presign");

                    addUploading(key);

                    try {
                        const headers = { ...(presign.requiredHeaders || {}) };
                        if (!headers["Content-Type"] && !headers["content-type"]) {
                            headers["Content-Type"] = type || "application/octet-stream";
                        }

                        const put = await fetch(presign.putUrl, {
                            method: "PUT",
                            headers,
                            body: file,
                        });

                        if (!put.ok) {
                            const t = await put.text().catch(() => "");
                            throw new Error(`Upload failed: ${put.status} ${t}`);
                        }

                        const ver = await verifyUpload({ key });
                        if (!isVerifyOk(ver)) throw new Error("Verify failed");

                        // оновлюємо локально і пушимо назовні
                        if (single) local = [key];
                        else local = uniqPush(local, key);

                        applyChange(() => local);
                    } finally {
                        removeUploading(key);
                    }

                    if (single) break;
                }
            } catch (e) {
                console.error(e);
                setErr(e?.message || "Не вдалося завантажити файл(и)");
            } finally {
                setBusy(false);
                resetInput();
            }
        },
        [single, folder, applyChange]
    );

    /* ===================== REMOVE ===================== */
    const removeAt = useCallback(
        (i) => applyChange((prev) => prev.filter((_, idx) => idx !== i)),
        [applyChange]
    );

    /* ===================== PRIMARY ===================== */
    const makePrimary = useCallback(
        (index) => applyChange((prev) => moveToFront(prev, index)),
        [applyChange]
    );

    /* ===================== MEDIA LIBRARY ===================== */
    const loadLibrary = useCallback(
        async ({ reset = false } = {}) => {
            setLibraryLoading(true);
            setErr("");

            try {
                const cursor = reset ? undefined : nextCursor;

                const data = await listMedia({
                    prefix: libraryPrefix, // <— ВАЖЛИВО: БЕЗ bsp/
                    cursor,
                    limit: 80,
                });

                const items = Array.isArray(data?.items) ? data.items : [];

                setLibraryItems((prev) => {
                    const merged = reset ? items : [...prev, ...items];
                    return dedupeByKey(merged);
                });

                setNextCursor(data?.nextCursor ?? undefined);
            } catch (e) {
                console.error(e);
                setErr(e?.message || "Не вдалося завантажити бібліотеку");
            } finally {
                setLibraryLoading(false);
            }
        },
        [libraryPrefix, nextCursor]
    );

    const openLibrary = useCallback(async () => {
        setLibraryOpen(true);
        setSearch("");
        setNextCursor(undefined);
        setLibraryItems([]);
        await loadLibrary({ reset: true });
    }, [loadLibrary]);

    const selectFromLibrary = useCallback(
        (key) => {
            const k = normalizeKey(key);
            if (!k) return;

            applyChange((prev) => {
                if (single) return [k];
                return prev.includes(k) ? prev : [...prev, k];
            });

            setLibraryOpen(false);
        },
        [applyChange, single]
    );

    return (
        <Box className={styles.pickerBox}>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                multiple={!single}
                onChange={(e) => handleFiles(e.target.files)}
                disabled={busy}
                hidden
            />

            <PickerToolbar
                title={title}
                busy={busy}
                onUploadClick={() => inputRef.current?.click()}
                onOpenLibrary={openLibrary}
            />

            <DropZone disabled={busy} onFiles={handleFiles} />

            <PreviewGrid
                value={value}
                thumbHeight={thumbHeight}
                toDisplaySrc={toDisplaySrc}
                uploadingKeys={uploadingKeys}
                onRemoveAt={removeAt}
                onMakePrimary={makePrimary}
                emptyText="Немає вибраних зображень"
            />

            {err && (
                <Alert severity="error" className={styles.error}>
                    {err}
                </Alert>
            )}

            <MediaLibraryDialog
                open={libraryOpen}
                onClose={() => setLibraryOpen(false)}
                search={search}
                setSearch={setSearch}
                loading={libraryLoading}
                items={libraryItems}
                nextCursor={nextCursor}
                onLoadMore={() => loadLibrary({ reset: false })}
                onSelectKey={selectFromLibrary}
                toDisplaySrc={toDisplaySrc}
            />
        </Box>
    );
}

